import React from 'react';
import { DocumentViewerCommonProps } from './helper';
import { isEmbeddableSpreadsheet } from '../../lib/Document';
import { PDFViewerProps } from './PDF/Strategies/PDFViewerProps';
import ExcelViewer, { ExcelViewerProps } from './Excel/ExcelViewer';
import PDFViewer from './PDF/PDFViewer';
import ConversionLoader from './PDF/ConversionLoader';
import { useDocumentPolling } from './hooks/useDocumentPolling';

export type DocumentViewerFacadeProps = DocumentViewerCommonProps &
  ExcelViewerProps &
  PDFViewerProps;

const DocumentViewerFacade = React.forwardRef<
  HTMLDivElement,
  DocumentViewerFacadeProps
>((props: DocumentViewerFacadeProps, ref) => {
  const {
    contentId,
    document,
    downloadable,
    downloadTrigger,
    onDownload,
    onLoad,
    previewDialog = false,
    rounded = false,
    trackingProperties,
    ...otherPDFViewerProps
  } = props;

  const { document: updatedDocument, isLoading } = useDocumentPolling({
    contentId,
    contentProcessingLog: otherPDFViewerProps.contentProcessingLog,
    initialDocument: document,
    userContactId: otherPDFViewerProps.userContactId,
  });

  if (isLoading) {
    return <ConversionLoader theaterMode={otherPDFViewerProps.theaterMode} />;
  }

  /**
   * Excel viewer is shown if:
   * - The document mime matches Excel mimes
   * - We known the document size and is under 25 MB
   */
  if (isEmbeddableSpreadsheet(updatedDocument)) {
    return (
      <ExcelViewer
        ref={ref}
        contentId={contentId}
        document={updatedDocument}
        downloadable={downloadable}
        downloadTrigger={downloadTrigger}
        onDownload={onDownload}
        onLoad={onLoad}
        previewDialog={previewDialog}
        rounded={rounded}
        trackingProperties={trackingProperties}
      />
    );
  }

  return (
    <PDFViewer
      contentId={contentId}
      document={updatedDocument}
      downloadable={downloadable}
      downloadTrigger={downloadTrigger}
      onDownload={onDownload}
      onLoad={onLoad}
      previewDialog={previewDialog}
      ref={ref}
      rounded={rounded}
      trackingProperties={trackingProperties}
      {...otherPDFViewerProps}
    />
  );
});

export default DocumentViewerFacade;
